import { BlurText } from "@/components/common/BlurText";
import { Button } from "@/components/ui/button";
import { useMe } from "@/hooks/useMe";
import { updateMe } from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { UserProfile } from "@clerk/clerk-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export function Profile() {
	const { t, i18n } = useTranslation();
	useMe();
	const queryClient = useQueryClient();

	const updateLanguage = useMutation({
		mutationFn: (lang: string) => updateMe({ language: lang }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.me });
		},
	});

	const handleLanguageChange = (lang: string) => {
		i18n.changeLanguage(lang);
		updateLanguage.mutate(lang);
	};

	const currentLang = i18n.language?.startsWith("pt") ? "pt" : "en";

	return (
		<div className="mx-auto max-w-4xl px-4 py-8 lg:px-8">
			<BlurText text={t("nav.profile")} className="mb-8 text-3xl font-bold text-primary" />

			{/* Language preference */}
			<div className="mb-8 rounded-xl border border-white/10 bg-bg-elevated p-6">
				<h2 className="mb-4 text-lg font-semibold text-primary">Language / Idioma</h2>
				<div className="flex gap-2">
					{[
						{ code: "en", label: "English" },
						{ code: "pt", label: "Português" },
					].map((lang) => (
						<Button
							key={lang.code}
							variant="ghost"
							onClick={() => handleLanguageChange(lang.code)}
							className={cn(
								"px-4 py-2",
								currentLang === lang.code
									? "bg-accent text-white hover:bg-accent-hover"
									: "text-secondary hover:text-primary",
							)}
						>
							{lang.label}
						</Button>
					))}
				</div>
			</div>

			{/* Clerk user profile */}
			<div className="rounded-xl border border-white/10 bg-bg-elevated p-6">
				<UserProfile
					appearance={{
						elements: {
							rootBox: "w-full",
							card: "bg-transparent shadow-none border-none",
							navbar: "hidden",
							pageScrollBox: "p-0",
						},
					}}
				/>
			</div>
		</div>
	);
}
